import { useMemo } from "react";
import dayjs from "dayjs";
import { ColumnDef } from "@tanstack/react-table";
import { DATE_TIME_FORMAT } from "@/v2/assets/utils";
import { XlsxExport, XlsxExportColumn } from "@/components/shared/xlsx-export";
import { formatString as formatStatus } from "@/v2/components/formatters/StatusFormatter";
import { formatString as formatChannel } from "@/v2/components/formatters/ChannelFormatter";
import { formatString as formatTopic } from "@/v2/components/formatters/TopicFormatter";
import { categoryToLabel } from "@/v2/components/formatters/CategoryFormatter";
import {
  ExploreAggInteractionRow,
  ExploreInteractionRow,
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
  isTeamArray,
} from "@/v2/entities/explore/Explore.model";
import {
  ExploreAggColumnType,
  ExploreColumnType,
  exploreGroupByToColumn,
  isExploreAggColumnType,
  isExploreColumnType,
  isExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { formatDuration } from "@/v2/components/formatters/DurationFromatter";
import {
  fixToTwo,
  formatWithCommas,
} from "@/v2/components/formatters/NumberFormatter";
import { getResolutionStatusText } from "@/v2/entities/explore/formatters/ResolutionStatusFormatter";
import { getResolvedReasonText } from "@/v2/entities/explore/formatters/ResolvedReasonFormatter";
import { getEscalationReasonText } from "@/v2/entities/explore/formatters/EscalationReasonFormatter";
import { getSkipReasonText } from "@/v2/entities/explore/formatters/SkipReasonFormatter";
import { getOriginAuthorText } from "@/v2/entities/explore/formatters/OriginAuthorFormatter";

const dateFormatter = (value: string) => dayjs(value).format(DATE_TIME_FORMAT);

const booleanFormatter = (value: boolean) => (value ? "Yes" : "No");

const numberFormatter = (value?: number) =>
  value == null ? "" : formatWithCommas(fixToTwo(+value));

const ratioFormatter = (value?: number) =>
  value == null ? "" : `${fixToTwo(value * 100)}%`;

const timeToResolutionFormatter = (value?: number) =>
  value == null ? "" : formatDuration(value * 60);

const timeToFirstResponseFormatter = (value?: number) =>
  value == null ? "" : formatDuration(value / 1000);

const exploreColumnFormatters: Record<
  ExploreColumnType,
  XlsxExportColumn["format"] | null
> = {
  tenantId: null,
  id: null,
  displayId: null,
  subject: null,
  description: null,
  createdAt: dateFormatter,
  updatedAt: dateFormatter,
  status: (value) => (typeof value === "string" ? formatStatus(value) : ""),
  channel: (value) => (typeof value === "string" ? formatChannel(value) : ""),
  topic: (value) => (typeof value === "string" ? formatTopic(value) : ""),
  subTopic: null,
  category: (value) =>
    typeof value === "string" ? categoryToLabel[value] ?? value : "",
  agentAssigned: null,
  team: null,
  teams: (value) =>
    isTeamArray(value) ? value.map((team) => team.name).join(", ") : "",
  tenantUser: null,
  tenantOrganization: null,
  tags: null,
  avgQaScore: numberFormatter,
  avgQaScoreRoundRange: numberFormatter,
  avgQuackMessageScoreRoundRange: numberFormatter,
  avgAutoQaScore: numberFormatter,
  avgSentiment: numberFormatter,
  lastSentiment: numberFormatter,
  avgQuackMessageScore: numberFormatter,
  csat: numberFormatter,
  csatComment: null,
  countPublicMessages: numberFormatter,
  countInternalMessages: numberFormatter,
  timeToResolution: (value) =>
    typeof value === "number" ? timeToResolutionFormatter(value) : "",
  timeToFirstResponse: (value) =>
    typeof value === "number" ? timeToFirstResponseFormatter(value) : "",
  resolutionStatus: (value) =>
    typeof value === "string" ? getResolutionStatusText(value) : "",
  escalationReason: (value) =>
    typeof value === "string" ? getEscalationReasonText(value) : "",
  skipReason: (value) =>
    typeof value === "string" ? getSkipReasonText(value) : "",
  resolvedReason: (value) =>
    typeof value === "string" ? getResolvedReasonText(value) : "",
  countQuackMessages: numberFormatter,
  isQuackEscalated: booleanFormatter,
  isQuackAnswered: booleanFormatter,
  collaborators: null,
  isFiniteState: booleanFormatter,
  validationStatus: booleanFormatter,
  agentAssignedId: null,
  ticketId: null,
  originAuthor: (value) => getOriginAuthorText(value),
  originUrl: null,
  scoreTitle: null,
  scoreContent: null,
  simulatorId: null,
  skillUsed: null,
  skillsUsed: null,
  isUserAuthenticated: booleanFormatter,
  isAnsweredByAui: booleanFormatter,
  avgCsat: numberFormatter,
};

const exploreAggColumnFormatters: Record<
  ExploreAggColumnType,
  XlsxExportColumn["format"] | null
> = {
  volume: null,
  volumeRatio: ratioFormatter,
  avgQuackMessages: numberFormatter,
  avgQaScore: numberFormatter,
  avgAutoQaScore: numberFormatter,
  avgSentiment: numberFormatter,
  avgTimeToResolution: (value) =>
    typeof value === "number" ? timeToResolutionFormatter(value) : "",
  avgTimeToFirstResponse: (value) =>
    typeof value === "number" ? timeToFirstResponseFormatter(value) : "",
  avgCsat: numberFormatter,
  avgPublicMessages: numberFormatter,
  avgInternalMessages: numberFormatter,
  countAgentsAssigned: numberFormatter,
  avgQaScoreByTitle: numberFormatter,
  avgQaScoreByContent: numberFormatter,
};

interface ExploreExportProps {
  source: ExploreInteractionType;
  groupBy?: string;
  columns: ColumnDef<ExploreInteractionRow>[];
  aggColumns: ColumnDef<ExploreAggInteractionRow>[];
  exploreInteractions?: ExploreInteractionRow[];
  exploreAggInteractions?: ExploreAggInteractionRow[];
}

function ExploreExport({
  source,
  groupBy,
  exploreInteractions,
  exploreAggInteractions,
  columns: exploreColumns,
  aggColumns: exploreAggColumns,
}: ExploreExportProps) {
  const { columns, data, filename, sheetName, buttonText } = useMemo(() => {
    const sourceWord = exploreInteractionTypeToLabel[source].plural;

    const columns: XlsxExportColumn[] = [];

    if (groupBy == null) {
      exploreColumns.forEach(({ id, header, size }) => {
        if (id == null) {
          return;
        }

        if (!isExploreColumnType(id) || exploreColumnFormatters[id] == null) {
          columns.push({
            key: id,
            header: header?.toString() ?? "Unknown",
            width: size ?? 20,
          });
          return;
        }

        columns.push({
          key: id,
          header: header?.toString() ?? "Unknown",
          width: size ?? 20,
          format: exploreColumnFormatters[id] ?? undefined,
        });
      });

      return {
        data: exploreInteractions ?? [],
        columns,
        filename: `explore-${source}`,
        sheetName: `${sourceWord}`,
        buttonText: `Export ${sourceWord} (${
          exploreInteractions?.length ?? 0
        })`,
      };
    }

    exploreAggColumns.forEach(({ id, header, size }) => {
      if (id == null) {
        return;
      }

      if (
        isExploreGroupByColumnType(id) &&
        exploreGroupByToColumn[id] != null
      ) {
        const format = exploreColumnFormatters[exploreGroupByToColumn[id]!];

        columns.push({
          key: id,
          header: header?.toString() ?? "Unknown",
          width: size ?? 20,
          format: (value) => {
            if (value == null) {
              return "Unclassified";
            }

            if (format == null) {
              return value;
            }

            return format(value);
          },
        });
        return;
      }

      if (
        !isExploreAggColumnType(id) ||
        exploreAggColumnFormatters[id] == null
      ) {
        columns.push({
          key: id,
          header: header?.toString() ?? "Unknown",
          width: size ?? 20,
        });
        return;
      }

      columns.push({
        key: id,
        header: header?.toString() ?? "Unknown",
        width: size ?? 20,
        format: exploreAggColumnFormatters[id] ?? undefined,
      });
    });

    return {
      data: exploreAggInteractions ?? [],
      columns,
      filename: `explore-${source}-${groupBy}`,
      sheetName: `${sourceWord} - ${groupBy}`.slice(0, 31),
      buttonText: `Export ${sourceWord} (Aggregated) ${
        exploreAggInteractions?.length ?? 0
      }`,
    };
  }, [
    exploreColumns,
    exploreAggColumns,
    exploreInteractions,
    exploreAggInteractions,
    groupBy,
    source,
  ]);

  return (
    <XlsxExport
      data={data}
      columns={columns}
      filename={filename}
      sheetName={sheetName}
      buttonText={buttonText}
      buttonVariant="outline"
      buttonSize="sm"
      disabled={data.length === 0}
    />
  );
}

export default ExploreExport;
